import { Component, inject, signal } from '@angular/core';
import { AbstractControlOptions, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthApiService } from '../../../services/auth-api.service';
import { ToastService } from '../../../services/toast.service';
import { ErrorService } from '../../../../../shared/services/error.service';
import { HttpErrorResponse } from '@angular/common/http';
import { LoadingController } from '@ionic/angular';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BrowserService } from '../../../services/in-app-services/browser.service';

@Component({
  selector: 'app-sign-up',
  templateUrl: './sign-up.component.html',
  styleUrls: ['./sign-up.component.scss'],
  standalone: false,
})
export class SignUpComponent {
  authApiService = inject(AuthApiService);
  toastService = inject(ToastService);
  errorService = inject(ErrorService);
  loadingCtrl = inject(LoadingController);
  router = inject(Router);
  translateService = inject(TranslateService);
  browserService = inject(BrowserService);

  signUpForm: FormGroup;
  showPassword = signal<boolean>(false);

  selectedLanguage = signal<string>(this.translateService.currentLang);
  private readonly languages = ['en', 'ge'];

  constructor(private fb: FormBuilder) {
    this.signUpForm = this.fb.group(
      {
        email: ['', [Validators.required, Validators.email]],
        name: ['', [Validators.required]],
        password: ['', [Validators.required, Validators.minLength(6)]],
        confirmPassword: ['', [Validators.required]],
        agreeToTerms: [false, [Validators.requiredTrue]],
      },
      <AbstractControlOptions>{ validator: this.passwordMatchValidator }
    );
  }

  toggleLanguage() {
    const currentIndex = this.languages.indexOf(this.selectedLanguage());
    const nextIndex = (currentIndex + 1) % this.languages.length;
    const nextLang = this.languages[nextIndex];
    
    this.translateService.use(nextLang);
    this.selectedLanguage.set(nextLang);
  }

  get email() {
    return this.signUpForm.get('email');
  }

  get name() {
    return this.signUpForm.get('name');
  }

  get password() {
    return this.signUpForm.get('password');
  }

  get confirmPassword() {
    return this.signUpForm.get('confirmPassword');
  }

  get agreeToTerms() {
    return this.signUpForm.get('agreeToTerms');
  }

  passwordMatchValidator(form: FormGroup) {
    return form.get('password')?.value === form.get('confirmPassword')?.value ? null : { mismatch: true };
  }

  passwordMismatch(): boolean {
    return this.password?.value !== this.confirmPassword?.value && this.confirmPassword?.value?.length > 0;
  }

  togglePassword() {
    this.showPassword.set(!this.showPassword());
  }

  openTerms(event: Event) {
    event.preventDefault();

    this.browserService.openTerms();
  }

  openPrivacy(event: Event) {
    event.preventDefault();

    this.browserService.openPrivacy();
  }

  onSubmit() {
    if (this.signUpForm.valid) {
      this.showLoading();

      this.authApiService
        .registerClient({
          email: this.email?.value,
          name: this.name?.value,
          password: this.password?.value,
          confirmPassword: this.confirmPassword?.value,
        })
        .subscribe({
          next: (response) => {
            this.toastService.success(this.translateService.instant('AUTH.SIGN_UP.SUCCESS'));
            this.signUpForm.reset();

            this.router.navigate(['/login']);

            this.stopLoading();
          },
          error: (error: HttpErrorResponse) => {
            this.errorService.handleError(error);

            this.stopLoading();
          },
        });
    }
  }

  private async showLoading() {
    const loading = await this.loadingCtrl.create({
      message: this.translateService.instant('COMMON.PLEASE_WAIT'),
      animated: true,
    });

    await loading.present();
  }

  private stopLoading() {
    this.loadingCtrl.dismiss();
  }
}
